import { cn, Icon, type IconProps, Text, type TextVariant } from '@mntn/ui-components'

type BadgeType = 'primary' | 'secondary' | 'success' | 'warning' | 'error'
type BadgeVariant = 'solid' | 'subtle' | 'dark'
type BadgeShape = 'rounded' | 'square'

const styleMap: Record<BadgeType, Record<BadgeVariant, string>> = {
  primary: {
    solid: 'bg-blue-600 text-header-inverse',
    subtle: 'bg-[#D7ECFF] text-blue-500',
    dark: 'text-header-inverse/80 bg-sub-screen-bg-inverse items-start pl-0',
  },
  secondary: {
    solid: 'bg-gray-600 text-header-inverse',
    subtle: 'bg-[#F3F4F7] text-header',
    dark: 'text-header-inverse/80 bg-sub-screen-bg-inverse items-start pl-0',
  },
  success: {
    solid: 'bg-green-600 text-header-inverse',
    subtle: 'bg-green-100 text-green-600',
    dark: 'text-header-inverse/80 bg-sub-screen-bg-inverse items-start pl-0',
  },
  warning: {
    solid: 'bg-yellow-500 text-header-inverse',
    subtle: 'bg-amber-100 text-yellow-800',
    dark: 'text-blue-400 bg-sub-screen-bg-inverse items-start pl-0',
  },
  error: {
    solid: 'bg-[#C80D25] text-header-inverse',
    subtle: 'bg-[#fceaeb] text-[#C80D25]',
    dark: 'text-red-300 bg-sub-screen-bg-inverse items-start pl-0',
  },
}

const shapeMap: Record<BadgeShape, string> = {
  rounded: 'rounded-full',
  square: 'rounded-sm',
}

export interface Props {
  label: string
  textVariant?: TextVariant
  iconProps?: IconProps
  className?: string
  type: BadgeType
  shape?: BadgeShape
  variant?: BadgeVariant
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const Badge: React.FC<Props> = ({
  type,
  textVariant = 'base-sm',
  variant = 'subtle',
  shape = 'rounded',
  label,
  iconProps,
  className,
}) => {
  const base = 'flex items-center gap-1.5 px-3 py-1 text-sm border-transparent'
  const colorClass = styleMap[type][variant]
  const shapeClass = shapeMap[shape]

  const { name: iconName, ref, ...iconPropsRest } = iconProps || {}

  return (
    <div className={cn(base, colorClass, shapeClass, 'min-w-0', className)}>
      <div className="flex items-center gap-1 truncate">
        {iconName && <Icon name={iconName} className="size-3 text-inherit flex-shrink-0" {...iconPropsRest} />}
        <Text variant={textVariant} className="text-inherit">
          {label}
        </Text>
      </div>
    </div>
  )
}
